import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { annotateSqlWithSyntaxError } from '@mode/shared/util-js';
import * as angular from 'angular';
import { ModeModelBase, ModeModelInjectables } from '../model';
import { ModelAccessorBase, ModelAccessorInjectables } from '../model_accessor';
export class QueryRunModel extends ModeModelBase {
  override _name = 'queryRun';

  parameters: any;
  limit_selected: any;
  running: boolean;
  state: string;
  warning: boolean;
  timeCompleted: any;
  reportDataUrl: any;
  reportQueryUrl: any;
  reportRunUrl: any;
  startedAt: any;
  createdAt: any;
  errorLineHtml: any;
  errorMessage: string;
  rendered_source: string;
  completed_at: string | number | Date;

  override idAttribute = 'token';
  override belongsTo = 'reportRun';
  override parentIdAttribute = 'token';
  override hasOne = [
    { name: 'result', idAttribute: 'id' },
    { name: 'query', idAttribute: 'token' },
    { name: 'account', as: 'executedBy', idAttribute: 'username' },
  ];
  override attributes = [
    { name: 'parameters', default: { limit: 100 } },
    { name: 'limit', default: true },
    { name: 'token', default: null },
    { name: 'state', default: 'new' },
    { name: 'source', default: '' },
    { name: 'raw_source', default: '' },
    { name: 'rendered_source', default: '' },
    { name: 'layout', default: '' },
    { name: 'table_view', default: '' },
    { name: 'error_code', default: '' },
    { name: 'error_type', default: '' },
    { name: 'error_message', default: '' },
    { name: 'errorMessage', default: '' },
    { name: 'errorDetail', default: '' },
    { name: 'dataSourceId', default: 'public' },
    { name: 'createdAt', default: '' },
    { name: 'localCreatedAt', default: '', abstract: true },
    { name: 'startedAt', default: '' },
    { name: 'completed_at', default: '' },
    { name: 'share', default: '' },
    { name: 'link', default: '' },
    { name: 'embedLink', default: '' },
    { name: 'iframeLink', default: '' },
    { name: 'limit_selected', default: true },
    { name: 'query_token', default: '' },
    { name: 'timeCompleted', default: '' },
    { name: 'data_source_id', default: null },
  ];

  constructor(
    injectables: ModeModelInjectables,
    private domSanitizer: DomSanitizer
  ) {
    super(injectables);
  }

  override initialize(resource) {
    if (angular.isDefined(resource) && angular.isDefined(resource.parameters)) {
      if (angular.isDefined(resource.parameters.limit) && angular.isDefined(resource.parameters.selected)) {
        // this is an intialization of an existing query run
        this.parameters.limit = resource.parameters.limit;
        this.limit_selected = resource.parameters.selected;
      } else if (angular.isDefined(resource.parameters.limit)) {
        // limit is set from api so make limit selected
        this.parameters.limit = resource.parameters.limit;
        this.limit_selected = true;
      } else {
        this.limit_selected = false;
      }

      this.generateErrorLine();
    }
    this.running = this.state === 'pending' || this.state === 'enqueued' || this.state === 'running';
    this.warning = this.state === 'failed' || this.state === 'cancelled';
    if (this.completed_at && !this.timeCompleted) {
      this.initTimeCompleted();
    }
    if (resource && resource.hasLink('result_web')) {
      this.reportDataUrl = resource.links('result_web').href;
    }
    if (resource && resource.hasLink('query_web')) {
      this.reportQueryUrl = resource.links('query_web').href;
    }
    if (resource && resource.hasLink('report_run_web')) {
      this.reportRunUrl = resource.links('report_run_web').href;
    }
  }

  initTimeCompleted() {
    const end = new Date(this.completed_at);
    const start = new Date(this.startedAt || this.createdAt);
    const elapsed = Math.max(0, end.getTime() - start.getTime());
    const hours = Math.floor(elapsed / 3600000);
    let minutes;
    let seconds;
    if (hours > 0) {
      minutes = Math.floor((elapsed % 3600000) / 60000);
      this.timeCompleted = hours + 'h' + (minutes > 0 ? ' ' + minutes + 'm' : '');
      return;
    }
    minutes = Math.floor(elapsed / 60000);
    if (minutes > 0) {
      seconds = Math.floor((elapsed % 60000) / 1000);
      this.timeCompleted = minutes + 'm' + (seconds > 0 ? ' ' + seconds + 's' : '');
      return;
    }
    seconds = Math.floor(elapsed / 1000);
    if (seconds > 0) {
      this.timeCompleted = seconds + 's';
      return;
    }
    this.timeCompleted = elapsed + 'ms';
  }

  generateErrorLine() {
    if (this.errorLineHtml) {
      return;
    }

    const result = annotateSqlWithSyntaxError({
      state: this.state,
      errorMessage: this.errorMessage,
      renderedSource: this.rendered_source,
    });

    if (result) {
      this.errorLineHtml = this.domSanitizer.bypassSecurityTrustHtml(result);
    }
  }
}

@Injectable({ providedIn: 'root' })
export class QueryRunModelAccessor extends ModelAccessorBase<QueryRunModel> {
  override name = 'queryRun';
  override idAttribute = 'token';
  override parentModel = 'reportRun';
  override parentIdAttribute = 'token';
  override createAttributes = ['account'];

  constructor(
    injectables: ModelAccessorInjectables,
    private domSanitizer: DomSanitizer
  ) {
    super(injectables, 'migrated', 'mode.queryRun');
  }

  createModel(resource, opts?) {
    const model = new QueryRunModel(this.injectables.modelInjectables, this.domSanitizer);
    model.$initialize(resource, opts);
    return model;
  }
}
